import { useState, useRef } from "react";
import { Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { SessionProvider, useSession } from "./shared/components/SessionProvider.jsx";
import RequireAuth from "./shared/components/RequireAuth.jsx";
import { LegalFooter } from "./shared/components/LegalLinks.jsx";
import RequireBilling from "./shared/components/RequireBilling.jsx";
import Login from "./shared/components/Login.jsx";
import SignUp from "./shared/components/SignUp.jsx";
import ForgotPassword from "./shared/components/ForgotPassword.jsx";
import ResetPassword from "./shared/components/ResetPassword.jsx";
import MagicLinkCallback from "./shared/components/MagicLinkCallback.jsx";
import Home from "./shared/components/Home.jsx";
import Settings from "./shared/components/Settings.jsx";
import Help from "./shared/components/Help.jsx";
import WorkspaceSwitcher from "./shared/components/WorkspaceSwitcher.jsx";
import Subscribe from "./shared/components/Subscribe.jsx";
import { usePlatformAdmin } from "./shared/hooks/usePlatformAdmin.js";
import { useWorkspaceAdmin } from "./shared/hooks/useWorkspaceAdmin.js";
import { useUserRole } from "./shared/hooks/useUserRole.js";
import BrandLogo from "./shared/components/BrandLogo.jsx";
import { useTheme } from "./shared/components/ThemeProvider.jsx";
import { displayName, initialsOf, avatarUrl } from "./shared/utils/userDisplay.js";
import { useClickOutside } from "./shared/hooks/useClickOutside.js";
import { VERSION_LABEL, BUILD_TIME } from "./shared/utils/version.js";

// Module registration — each module exposes its routes via its index.js public
// entry only. registerModule preserves the auth/billing/admin gating the app
// had before the modular refactor.
import { registerModule } from "./registry.jsx";
import { distributionMapRoutes } from "./modules/distribution-map/index.js";
import { posFulfillmentRoutes } from "./modules/pos-fulfillment/index.js";
import { depletionsInventoryRoutes } from "./modules/depletions-inventory/index.js";
import { salesForecastingRoutes } from "./modules/sales-forecasting/index.js";
import { brandAmbassadorRoutes } from "./modules/brand-ambassador/index.js";
import { productionPlanningRoutes } from "./modules/production-planning/index.js";
import { incentiveTrackerRoutes } from "./modules/incentive-tracker/index.js";
import { distributorOrdersRoutes } from "./modules/distributor-orders/index.js";
import { productSpecificationsRoutes } from "./modules/product-specifications/index.js";
import { retailProductFinderRoutes } from "./modules/retail-product-finder/index.js";
import { systemSettingsRoutes } from "./modules/system-settings/index.js";
import { adminRoutes } from "./modules/admin/index.js";

// Customer-facing module list for the support ticket form — collected straight
// from the module folders: any module index.js that exports `moduleMeta` shows
// up automatically (no per-module wiring here). Composition root only.
import { setModuleCatalog } from "./shared/utils/moduleCatalog.js";
import { setDashboardProviders } from "./shared/utils/dashboardRegistry.js";
const _moduleMetas = import.meta.glob("./modules/*/index.js", { eager: true });
setModuleCatalog(Object.values(_moduleMetas).map((m) => m.moduleMeta).filter(Boolean));
// Operations-hub (Home) KPI/signal providers — any module index.js that exports
// `dashboardProvider` contributes automatically (no per-module wiring here).
setDashboardProviders(Object.values(_moduleMetas).map((m) => m.dashboardProvider).filter(Boolean));

// Role badge colors. Keys are normalized role strings (lowercase, spaces→_).
// Staff roles (platform_admins) and tenant roles (account_members) both appear.
// Every hue is a THEME TOKEN, not a hex. The literals these replaced were
// fixed values that only ever suited dark theme — "owner" measured 3.31:1 in
// dark and worse in light. The categorical set is defined in styles.css and
// carries a per-theme value (backlog 150).
const ROLE_PILL = {
  superadmin:  { bg: "var(--error-soft)",      fg: "var(--error)" },   // platform staff — red
  super_admin: { bg: "var(--error-soft)",      fg: "var(--error)" },
  support:     { bg: "var(--cat-blue-soft)",   fg: "var(--cat-blue)" },   // platform support — blue
  owner:       { bg: "var(--cat-violet-soft)", fg: "var(--cat-violet)" }, // workspace owner — violet
  admin:       { bg: "var(--o-tint)",          fg: "var(--o-text)" },     // workspace admin — brand orange
  member:      { bg: "var(--cat-slate-soft)",  fg: "var(--cat-slate)" },  // member — slate
};
const ROLE_PILL_DEFAULT = { bg: "var(--cat-slate-soft)", fg: "var(--cat-slate)" };

function RolePill({ role, style }) {
  const c = ROLE_PILL[String(role).toLowerCase().replace(/\s+/g, "_")] || ROLE_PILL_DEFAULT;
  return (
    <span className="role-pill" style={{ background: c.bg, color: c.fg, ...style }}>{role}</span>
  );
}

function UserMenu() {
  const { user, signOut } = useSession();
  const { isWorkspaceAdmin } = useWorkspaceAdmin();
  const role = useUserRole();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useClickOutside(menuRef, () => setOpen(false), open);
  if (!user) return null;
  const avatar = avatarUrl(user);

  async function handleLogout() {
    setOpen(false);
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="usermenu" ref={menuRef}>
      <button className="usermenu-trigger" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
        <span className="avatar">{avatar ? <img src={avatar} alt="" /> : initialsOf(user)}</span>
        {/* Class, not an inline style: the mobile rule in styles.css collapses
            this trigger to the avatar alone (backlog APP-434), and an inline
            `display` beats any stylesheet declaration short of !important. */}
        <span className="usermenu-id">
          <span className="uname">{displayName(user)}</span>
          {role && <RolePill role={role} style={{ fontSize: 10, padding: "1px 8px", marginTop: 2 }} />}
        </span>
        <span className="caret" aria-hidden="true">▾</span>
      </button>
      {open && (
          <div className="usermenu-pop" role="menu">
            <div className="usermenu-head">
              <div className="uname">{displayName(user)}</div>
              <div className="uemail muted">{user.email}</div>
              {role && <RolePill role={role} style={{ marginTop: 6 }} />}
            </div>
            <button className="usermenu-item" role="menuitem" onClick={() => { setOpen(false); navigate("/account"); }}>
              Account settings
            </button>
            {isWorkspaceAdmin && (
              <button className="usermenu-item" role="menuitem" onClick={() => { setOpen(false); navigate("/system-settings"); }}>
                System Settings
              </button>
            )}
            <button className="usermenu-item" role="menuitem" onClick={handleLogout}>
              Log out
            </button>
          </div>
      )}
    </div>
  );
}

function HelpMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useClickOutside(menuRef, () => setOpen(false), open);
  const go = (tab) => { setOpen(false); navigate(`/help?tab=${tab}`); };
  return (
    <div className="usermenu" ref={menuRef}>
      <button className="help-trigger" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} title="Help">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><path d="M9.1 9.2a3 3 0 015.8 1c0 2-3 2.2-3 4" /><path d="M12 17h.01" />
        </svg>
        <span>Help</span>
      </button>
      {open && (
          <div className="usermenu-pop" role="menu">
            <button className="usermenu-item" role="menuitem" onClick={() => go("docs")} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 3v5h5" /><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M8 13h8M8 17h6" />
              </svg>
              User Documentation
            </button>
            <button className="usermenu-item" role="menuitem" onClick={() => go("tickets")} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 5h16v14H4z" /><path d="M4 13h4l2 3h4l2-3h4" />
              </svg>
              My help tickets
            </button>
            <button className="usermenu-item" role="menuitem" onClick={() => go("submit")} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4z" />
              </svg>
              Submit a ticket
            </button>
          </div>
      )}
    </div>
  );
}

function NavThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="nav-toggle" role="group" aria-label="Theme">
      <button aria-pressed={theme === "light"} title="Light theme" onClick={() => setTheme("light")}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      </button>
      <button aria-pressed={theme === "dark"} title="Dark theme" onClick={() => setTheme("dark")}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
        </svg>
      </button>
    </div>
  );
}

// Owner/staff-only entry to the internal Admin module. Shown in the top bar
// (next to the user/role pill) rather than the user dropdown so it's clearly
// owner tooling, not a customer-facing menu item. Visibility unchanged: gated
// on platform_admins via usePlatformAdmin(); server RLS is the hard boundary.
function AdminNavButton() {
  const { isAdmin } = usePlatformAdmin();
  const navigate = useNavigate();
  if (!isAdmin) return null;
  return (
    <button className="help-trigger topnav-admin" onClick={() => navigate("/admin")} title="BrandCommand Admin">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 2 4 5v6c0 5 3.4 8.3 8 11 4.6-2.7 8-6 8-11V5z" />
      </svg>
      <span>Admin</span>
    </button>
  );
}

function AppHeader() {
  return (
    <header className="topnav">
      <div className="topnav-left">
        <Link to="/" className="topnav-brand" aria-label="Brand Command home">
          <BrandLogo cube={34} word={16} tagline={false} gap={9} />
        </Link>
        <span className="topnav-version" title={`Build ${BUILD_TIME}`}>{VERSION_LABEL}</span>
      </div>
      <div className="topnav-right">
        {/* Renders nothing unless the user is in more than one workspace. */}
        <WorkspaceSwitcher />
        <NavThemeToggle />
        <HelpMenu />
        <AdminNavButton />
        <UserMenu />
      </div>
    </header>
  );
}

const AUTH_PATHS = new Set([
  "/login", "/signup", "/forgot-password", "/reset-password", "/auth/callback",
]);

// Routes that render a whole page of their own and must NOT wear the Brand
// Command header. Today that's the Harbor Ridge demo site — a stand-in for a
// customer's own website used to demo the POS ordering embed and capture it for
// marketing. It is still staff-gated by the admin module's route registration;
// this only removes the chrome, never the guard.
const CHROMELESS_PATHS = new Set(["/harbor-ridge"]);

export default function App() {
  const { pathname } = useLocation();
  const isAuth = AUTH_PATHS.has(pathname);
  const chromeless = CHROMELESS_PATHS.has(pathname);
  return (
    <SessionProvider>
      {!isAuth && !chromeless && <AppHeader />}
      <main className="appshell">
        <Routes>
          {/* Public auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<MagicLinkCallback />} />

          {/* Shell routes (authenticated; no billing gate) */}
          <Route path="/subscribe" element={<RequireAuth><Subscribe /></RequireAuth>} />
          <Route path="/account" element={<RequireAuth><Settings /></RequireAuth>} />
          <Route path="/help" element={<RequireAuth><Help /></RequireAuth>} />
          {/* Back-compat for the old separate route */}
          <Route path="/preferences" element={<Navigate to="/account?tab=appearance" replace />} />
          {/* Back-compat: the Distribution Map list lived at /dashboard back when
              this module was the whole product. Kept so existing bookmarks and
              any link we've already sent out still land somewhere correct. */}
          <Route path="/dashboard" element={<Navigate to="/distribution-map" replace />} />

          {/* Shell home (authenticated + billing) — the module launcher */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <RequireBilling>
                  <Home />
                </RequireBilling>
              </RequireAuth>
            }
          />

          {/* Product modules (all authenticated users) */}
          {registerModule(distributionMapRoutes, { roles: ["admin", "user"] })}
          {registerModule(posFulfillmentRoutes, { roles: ["admin", "user"] })}
          {registerModule(depletionsInventoryRoutes, { roles: ["admin", "user"] })}
          {registerModule(salesForecastingRoutes, { roles: ["admin", "user"] })}
          {registerModule(distributorOrdersRoutes, { roles: ["admin", "user"] })}
          {registerModule(incentiveTrackerRoutes, { roles: ["admin", "user"] })}
          {registerModule(productSpecificationsRoutes, { roles: ["admin", "user"] })}
          {registerModule(brandAmbassadorRoutes, { roles: ["admin", "user"] })}
          {registerModule(productionPlanningRoutes, { roles: ["admin", "user"] })}

          {/* Customer company setup (workspace owner/admin only) */}
          {/* Editing the public store finder changes what customers see on the
              tenant's own website, so it's owner/admin only — same gate as
              System Settings, not the general product-user gate above. */}
          {registerModule(retailProductFinderRoutes, { roles: ["workspace_admin"] })}
          {registerModule(systemSettingsRoutes, { roles: ["workspace_admin"] })}

          {/* Admin module (super_admin only — invisible to all other roles) */}
          {registerModule(adminRoutes, { roles: ["super_admin"] })}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {/* Persistent, everywhere except the auth screens (which carry the consent
          line instead) and the chromeless demo site, where our footer would
          break the illusion of a customer's own page. */}
      {!isAuth && !chromeless && <LegalFooter />}
    </SessionProvider>
  );
}
